import { useEffect, useRef, useState, useCallback } from 'react';
import type { WSMessage, AuctionState, Item, Bid, ShippingConfig, BinSale } from '../types';
import { ensureFreshToken } from '../api/client';

import { getWsUrl } from '../config/env';

type BidStatus = 'idle' | 'pending' | 'accepted' | 'rejected';
type BidRejectionReason = string | null;
type BinPurchaseStatus = 'idle' | 'pending' | 'success' | 'failed';
type BinPurchaseFailReason = string | null;
type GiveawayEntryStatus = 'idle' | 'pending' | 'entered' | 'failed';

interface ActiveGiveaway {
  id: string;
  itemId: string;
  status: string;
  entryCount: number;
  startedAt?: string;
  winnerId?: string;
  winnerUsername?: string;
  item?: Item;
}

interface BuyerValidation {
  canBid: boolean;
  missingPaymentMethod: boolean;
  missingAddress: boolean;
  missingVerification: boolean;
  hasFailedPayment: boolean;
}

interface UseBuyerWebSocketReturn {
  isConnected: boolean;
  hasReceivedState: boolean;
  viewerCount: number;
  onDeckItem: Item | null;
  activeAuction: AuctionState | null;
  activeBinSale: BinSale | null;
  recentBids: Bid[];
  auctionEnded: { winnerId: string | null; winnerUsername: string | null; winningBid: number | null } | null;
  binSaleEnded: { reason: string; totalSold: number } | null;
  showEnded: { reason: string } | null;
  bidStatus: BidStatus;
  bidRejectionReason: BidRejectionReason;
  binPurchaseStatus: BinPurchaseStatus;
  binPurchaseFailReason: BinPurchaseFailReason;
  shippingConfig: ShippingConfig | null;
  buyerValidation: BuyerValidation;
  activeGiveaway: ActiveGiveaway | null;
  giveawayEntryStatus: GiveawayEntryStatus;
  giveawayEnded: { giveawayId: string; winnerId?: string; winnerUsername?: string; entryCount: number; cancelled: boolean } | null;
  connect: (showId: string, userId?: string, username?: string) => void;
  disconnect: () => void;
  placeBid: (auctionId: string, bidType: 'AUTO_INCREMENT' | 'CUSTOM' | 'MAX_BID', amount?: number) => void;
  userMaxBidAmount: number | null;
  lastBidFlash: { username: string; amount: number } | null;
  purchaseBin: (saleId: string) => void;
  enterGiveaway: (giveawayId: string) => void;
  refreshValidation: () => void;
  broadcasterPaused: boolean;
}

export function useBuyerWebSocket(): UseBuyerWebSocketReturn {
  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [hasReceivedState, setHasReceivedState] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [onDeckItem, setOnDeckItem] = useState<Item | null>(null);
  const [activeAuction, setActiveAuction] = useState<AuctionState | null>(null);
  const [activeBinSale, setActiveBinSale] = useState<BinSale | null>(null);
  const [recentBids, setRecentBids] = useState<Bid[]>([]);
  const [auctionEnded, setAuctionEnded] = useState<{
    winnerId: string | null;
    winnerUsername: string | null;
    winningBid: number | null;
  } | null>(null);
  const [showEnded, setShowEnded] = useState<{ reason: string } | null>(null);
  const [binSaleEnded, setBinSaleEnded] = useState<{ reason: string; totalSold: number } | null>(null);
  const [bidStatus, setBidStatus] = useState<BidStatus>('idle');
  const [bidRejectionReason, setBidRejectionReason] = useState<BidRejectionReason>(null);
  const [userMaxBidAmount, setUserMaxBidAmount] = useState<number | null>(null);
  const [lastBidFlash, setLastBidFlash] = useState<{ username: string; amount: number } | null>(null);
  const lastBidFlashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [binPurchaseStatus, setBinPurchaseStatus] = useState<BinPurchaseStatus>('idle');
  const [binPurchaseFailReason, setBinPurchaseFailReason] = useState<BinPurchaseFailReason>(null);
  const [shippingConfig, setShippingConfig] = useState<ShippingConfig | null>(null);
  const [buyerValidation, setBuyerValidation] = useState<BuyerValidation>({
    canBid: true,
    missingPaymentMethod: false,
    missingAddress: false,
    missingVerification: false,
    hasFailedPayment: false,
  });
  const [activeGiveaway, setActiveGiveaway] = useState<ActiveGiveaway | null>(null);
  const [giveawayEntryStatus, setGiveawayEntryStatus] = useState<GiveawayEntryStatus>('idle');
  const [giveawayEnded, setGiveawayEnded] = useState<{
    giveawayId: string;
    winnerId?: string;
    winnerUsername?: string;
    entryCount: number;
    cancelled: boolean;
  } | null>(null);
  const [broadcasterPaused, setBroadcasterPaused] = useState(false);

  const connect = useCallback(async (showId: string, userId?: string, username?: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    const params = new URLSearchParams();

    // Prefer JWT token for authentication - ensure it's fresh
    const token = await ensureFreshToken();
    if (token) {
      params.set('token', token);
    } else if (userId) {
      // Fall back to user_id for backwards compatibility
      params.set('user_id', userId);
      if (username) {
        params.set('username', username);
      }
    }

    const ws = new WebSocket(`${getWsUrl()}/ws/show/${showId}?${params.toString()}`);

    ws.onopen = () => {
      console.log('WebSocket connected');
      setIsConnected(true);
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setIsConnected(false);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.onmessage = (event) => {
      // Server may batch multiple messages separated by newlines
      const messages = (event.data as string).split('\n').filter(Boolean);
      for (const msgStr of messages) {
        try {
          const message: WSMessage = JSON.parse(msgStr);
          handleMessage(message);
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e, msgStr);
        }
      }
    };

    wsRef.current = ws;
  }, []);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setHasReceivedState(false);
  }, []);

  const placeBid = useCallback((auctionId: string, bidType: 'AUTO_INCREMENT' | 'CUSTOM' | 'MAX_BID', amount?: number) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return;
    }

    setBidStatus('pending');

    const payload: { auction_id: string; bid_type: string; amount?: number } = {
      auction_id: auctionId,
      bid_type: bidType,
    };

    if ((bidType === 'CUSTOM' || bidType === 'MAX_BID') && amount !== undefined) {
      payload.amount = amount;
    }

    wsRef.current.send(JSON.stringify({
      type: 'PLACE_BID',
      payload,
    }));
  }, []);

  const purchaseBin = useCallback((saleId: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return;
    }

    setBinPurchaseStatus('pending');

    wsRef.current.send(JSON.stringify({
      type: 'BIN_PURCHASE',
      payload: {
        sale_id: saleId,
      },
    }));
  }, []);

  const enterGiveaway = useCallback((giveawayId: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return;
    }

    setGiveawayEntryStatus('pending');

    wsRef.current.send(JSON.stringify({
      type: 'ENTER_GIVEAWAY',
      payload: {
        giveaway_id: giveawayId,
      },
    }));
  }, []);

  const refreshValidation = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.log('WebSocket not connected, cannot refresh validation');
      return;
    }

    console.log('Requesting validation refresh');
    wsRef.current.send(JSON.stringify({
      type: 'REFRESH_VALIDATION',
      payload: {},
    }));
  }, []);

  const handleMessage = (message: WSMessage) => {
    console.log('WS Message:', message.type, message.payload);

    switch (message.type) {
      case 'CONNECTION_ACK': {
        const payload = message.payload as { viewer_count: number };
        setViewerCount(payload.viewer_count);
        break;
      }
      case 'SHOW_STATE': {
        const payload = message.payload as {
          viewer_count: number;
          current_item: {
            id: string;
            name: string;
            description: string;
            image_url: string;
            starting_price?: number;
            weight_oz?: number;
            item_number?: number;
          } | null;
          auction: {
            id: string;
            item_id: string;
            current_price: number;
            highest_bidder_id: string | null;
            highest_bidder_username: string | null;
            bid_count: number;
            ends_at: string;
            milliseconds_remaining: number;
            auction_time_seconds: number;
            counterbid_time_seconds: number;
            sudden_death_enabled: boolean;
          } | null;
          bin_sale: {
            id: string;
            item_id: string;
            base_price: number;
            final_price: number;
            sale_enabled: boolean;
            sale_type?: string;
            sale_amount?: number;
            initial_quantity: number;
            remaining_quantity: number;
            status: string;
            item?: {
              id: string;
              name: string;
              description?: string;
              image_url?: string;
              starting_price?: number;
              weight_oz?: number;
              item_number?: number;
            };
          } | null;
          last_auction_result: {
            auction_id: string;
            winner_id: string;
            winner_username: string;
            winning_bid: number;
            has_winner: boolean;
          } | null;
          shipping_config: {
            strategy: string;
            flat_rate_amount?: number;
            flat_rate_first_item_amount?: number;
            flat_rate_additional_item_amount?: number;
            origin_zip?: string;
            carrier?: string;
            carrier_service?: string;
            rate_card?: {
              bands: Array<{
                name: string;
                maxZipDistance: number | null;
                zipPrefixes?: string[] | null;
                rates: Array<{ maxOz: number; cost: number }>;
              }>;
            } | null;
            rate_card_generated_at?: string;
            free_shipping_threshold_enabled: boolean;
            free_shipping_threshold_amount?: number;
            max_shipping_cap_enabled: boolean;
            max_shipping_cap_amount?: number;
            zones?: Array<{
              zip_prefix_start: string;
              zip_prefix_end: string;
              base_rate: number;
            }>;
          } | null;
          active_giveaway?: {
            id: string;
            item_id: string;
            status: string;
            entry_count: number;
            started_at?: string;
            item?: {
              id: string;
              name: string;
              description?: string;
              image_url?: string;
              item_number?: number;
            };
          } | null;
          // Buyer validation flags from server
          can_bid?: boolean;
          missing_payment_method?: boolean;
          missing_address?: boolean;
          missing_verification?: boolean;
          has_failed_payment?: boolean;
        };
        setViewerCount(payload.viewer_count);

        // Map current_item from snake_case
        if (payload.current_item) {
          setOnDeckItem({
            id: payload.current_item.id,
            name: payload.current_item.name,
            description: payload.current_item.description,
            imageUrl: payload.current_item.image_url,
            startingPrice: payload.current_item.starting_price || 0,
            weightOz: payload.current_item.weight_oz,
            itemNumber: payload.current_item.item_number,
          });
        } else {
          setOnDeckItem(null);
        }

        // Map shipping_config from snake_case
        if (payload.shipping_config) {
          setShippingConfig({
            strategy: payload.shipping_config.strategy as 'FREE' | 'FLAT_RATE' | 'WEIGHT_BASED',
            flatRateAmount: payload.shipping_config.flat_rate_amount,
            flatRateFirstItemAmount: payload.shipping_config.flat_rate_first_item_amount,
            flatRateAdditionalItemAmount: payload.shipping_config.flat_rate_additional_item_amount,
            originZip: payload.shipping_config.origin_zip,
            carrier: payload.shipping_config.carrier as 'USPS' | 'FEDEX' | 'UPS' | undefined,
            carrierService: payload.shipping_config.carrier_service,
            rateCard: payload.shipping_config.rate_card?.bands ? {
              bands: payload.shipping_config.rate_card.bands.map(b => ({
                name: b.name,
                maxZipDistance: b.maxZipDistance,
                zipPrefixes: b.zipPrefixes ?? null,
                rates: b.rates,
              })),
            } : undefined,
            rateCardGeneratedAt: payload.shipping_config.rate_card_generated_at,
            freeShippingThresholdEnabled: payload.shipping_config.free_shipping_threshold_enabled,
            freeShippingThresholdAmount: payload.shipping_config.free_shipping_threshold_amount,
            maxShippingCapEnabled: payload.shipping_config.max_shipping_cap_enabled,
            maxShippingCapAmount: payload.shipping_config.max_shipping_cap_amount,
            zones: payload.shipping_config.zones?.map(z => ({
              zipPrefixStart: z.zip_prefix_start,
              zipPrefixEnd: z.zip_prefix_end,
              baseRate: z.base_rate,
            })),
          });
        }

        // Update buyer validation flags from server
        setBuyerValidation({
          canBid: payload.can_bid !== false, // Default to true if not provided
          missingPaymentMethod: payload.missing_payment_method === true,
          missingAddress: payload.missing_address === true,
          missingVerification: payload.missing_verification === true,
          hasFailedPayment: payload.has_failed_payment === true,
        });

        // Map auction from snake_case
        if (payload.auction) {
          setActiveAuction({
            id: payload.auction.id,
            itemId: payload.auction.item_id,
            currentPrice: payload.auction.current_price,
            highestBidderId: payload.auction.highest_bidder_id,
            highestBidderUsername: payload.auction.highest_bidder_username,
            bidCount: payload.auction.bid_count,
            endsAt: payload.auction.ends_at,
            millisecondsRemaining: payload.auction.milliseconds_remaining,
            auctionTimeSeconds: payload.auction.auction_time_seconds,
            counterbidTimeSeconds: payload.auction.counterbid_time_seconds,
            suddenDeathEnabled: payload.auction.sudden_death_enabled,
          });
          setAuctionEnded(null); // Clear any previous auction result
          setActiveBinSale(null); // Clear BIN sale when auction is active
        } else {
          setActiveAuction(null);

          // If no active auction, check for last auction result
          if (payload.last_auction_result) {
            setAuctionEnded({
              winnerId: payload.last_auction_result.has_winner ? payload.last_auction_result.winner_id : null,
              winnerUsername: payload.last_auction_result.has_winner ? payload.last_auction_result.winner_username : null,
              winningBid: payload.last_auction_result.has_winner ? payload.last_auction_result.winning_bid : null,
            });
          }
        }

        // Map bin_sale from snake_case
        if (payload.bin_sale) {
          setActiveBinSale({
            id: payload.bin_sale.id,
            itemId: payload.bin_sale.item_id,
            basePrice: payload.bin_sale.base_price,
            finalPrice: payload.bin_sale.final_price,
            saleEnabled: payload.bin_sale.sale_enabled,
            saleType: payload.bin_sale.sale_type as 'PERCENT' | 'AMOUNT' | undefined,
            saleAmount: payload.bin_sale.sale_amount,
            initialQuantity: payload.bin_sale.initial_quantity,
            remainingQuantity: payload.bin_sale.remaining_quantity,
            status: payload.bin_sale.status as 'PENDING' | 'LIVE' | 'ENDED' | 'CANCELLED',
            item: payload.bin_sale.item ? {
              id: payload.bin_sale.item.id,
              name: payload.bin_sale.item.name,
              description: payload.bin_sale.item.description || '',
              imageUrl: payload.bin_sale.item.image_url || '',
              startingPrice: payload.bin_sale.item.starting_price || 0,
              weightOz: payload.bin_sale.item.weight_oz,
              itemNumber: payload.bin_sale.item.item_number,
            } : undefined,
          });
          setActiveAuction(null); // Clear auction when BIN sale is active
          setAuctionEnded(null);
          setBinSaleEnded(null);
        } else if (!payload.auction) {
          // Only clear BIN sale if there's no auction either
          setActiveBinSale(null);
        }

        // Map active_giveaway from snake_case
        if (payload.active_giveaway) {
          setActiveGiveaway({
            id: payload.active_giveaway.id,
            itemId: payload.active_giveaway.item_id,
            status: payload.active_giveaway.status,
            entryCount: payload.active_giveaway.entry_count,
            startedAt: payload.active_giveaway.started_at,
            item: payload.active_giveaway.item ? {
              id: payload.active_giveaway.item.id,
              name: payload.active_giveaway.item.name,
              description: payload.active_giveaway.item.description || '',
              imageUrl: payload.active_giveaway.item.image_url || '',
              startingPrice: 0,
              itemNumber: payload.active_giveaway.item.item_number,
            } : undefined,
          });
          setGiveawayEnded(null);
        } else {
          setActiveGiveaway(null);
        }

        // Mark that we've received the initial state
        setHasReceivedState(true);
        break;
      }
      case 'ITEM_ON_DECK': {
        const payload = message.payload as {
          id: string;
          name: string;
          description: string;
          image_url: string;
          starting_price?: number;
          weight_oz?: number;
          item_number?: number;
        };
        setOnDeckItem({
          id: payload.id,
          name: payload.name,
          description: payload.description,
          imageUrl: payload.image_url,
          startingPrice: payload.starting_price || 0,
          weightOz: payload.weight_oz,
          itemNumber: payload.item_number,
        });
        setActiveAuction(null);
        setAuctionEnded(null);
        setActiveBinSale(null);
        setBinSaleEnded(null);
        break;
      }
      case 'AUCTION_STARTED': {
        const payload = message.payload as {
          id: string;
          item_id: string;
          current_price: number;
          highest_bidder_id: string | null;
          highest_bidder_username: string | null;
          bid_count: number;
          ends_at: string;
          milliseconds_remaining: number;
          auction_time_seconds: number;
          counterbid_time_seconds: number;
          sudden_death_enabled: boolean;
        };
        setActiveAuction({
          id: payload.id,
          itemId: payload.item_id,
          currentPrice: payload.current_price,
          highestBidderId: payload.highest_bidder_id,
          highestBidderUsername: payload.highest_bidder_username,
          bidCount: payload.bid_count,
          endsAt: payload.ends_at,
          millisecondsRemaining: payload.milliseconds_remaining,
          auctionTimeSeconds: payload.auction_time_seconds,
          counterbidTimeSeconds: payload.counterbid_time_seconds,
          suddenDeathEnabled: payload.sudden_death_enabled,
        });
        setRecentBids([]);
        setAuctionEnded(null);
        break;
      }
      case 'NEW_BID': {
        const payload = message.payload as {
          auction_id: string;
          bidder_id: string;
          bidder_username: string;
          amount: number;
          bid_count: number;
          milliseconds_remaining: number;
          triggered_by_id?: string;
          triggered_by_username?: string;
        };
        setActiveAuction((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            currentPrice: payload.amount,
            highestBidderId: payload.bidder_id,
            highestBidderUsername: payload.bidder_username,
            bidCount: payload.bid_count,
            millisecondsRemaining: payload.milliseconds_remaining,
          };
        });
        const newBid: Bid = {
          auctionId: payload.auction_id,
          bidderId: payload.bidder_id,
          bidderName: payload.bidder_username || '',
          amount: payload.amount,
          timestamp: new Date().toISOString(),
        };
        setRecentBids((prev) => {
          // Check if this exact bid already exists (same auction, bidder, amount)
          const isDuplicate = prev.some(
            (b) => b.auctionId === newBid.auctionId &&
                   b.bidderId === newBid.bidderId &&
                   b.amount === newBid.amount
          );
          if (isDuplicate) return prev;
          return [newBid, ...prev].slice(0, 10);
        });
        // Flash the challenger's bid when a max bid auto-countered
        if (payload.triggered_by_username) {
          if (lastBidFlashTimer.current) clearTimeout(lastBidFlashTimer.current);
          setLastBidFlash({ username: payload.triggered_by_username, amount: payload.amount });
          lastBidFlashTimer.current = setTimeout(() => setLastBidFlash(null), 2600);
        }
        break;
      }
      case 'MAX_BID_SET': {
        const payload = message.payload as { max_bid_amount: string; current_price: string; bid_count: number };
        setUserMaxBidAmount(parseFloat(payload.max_bid_amount));
        setBidStatus('accepted');
        setTimeout(() => setBidStatus('idle'), 1000);
        break;
      }
      case 'MAX_BID_OUTBID': {
        setUserMaxBidAmount(null);
        setBidStatus('rejected');
        setBidRejectionReason('Your max bid was outbid');
        setTimeout(() => { setBidStatus('idle'); setBidRejectionReason(null); }, 3000);
        break;
      }
      case 'BID_ACCEPTED': {
        setBidStatus('accepted');
        setTimeout(() => setBidStatus('idle'), 1000);
        break;
      }
      case 'BID_REJECTED': {
        const payload = message.payload as { reason: string };
        console.warn('Bid rejected:', payload.reason);
        setBidStatus('rejected');
        setBidRejectionReason(payload.reason);
        setTimeout(() => {
          setBidStatus('idle');
          setBidRejectionReason(null);
        }, 3000);
        break;
      }
      case 'AUCTION_ENDING_SOON': {
        // Server sends seconds_remaining, convert to milliseconds and sync timer
        const payload = message.payload as { auction_id: string; seconds_remaining: number };
        const msRemaining = (payload.seconds_remaining || 0) * 1000;
        if (msRemaining > 0) {
          setActiveAuction((prev) => {
            if (!prev) return null;
            return { ...prev, millisecondsRemaining: msRemaining };
          });
        }
        break;
      }
      case 'AUCTION_ENDED': {
        const payload = message.payload as {
          auction_id: string;
          winner_id: string;
          winner_username: string;
          winning_bid: number;
          has_winner: boolean;
        };
        setAuctionEnded({
          winnerId: payload.has_winner ? payload.winner_id : null,
          winnerUsername: payload.has_winner ? payload.winner_username : null,
          winningBid: payload.has_winner ? payload.winning_bid : null,
        });
        setActiveAuction(null);
        setUserMaxBidAmount(null);
        setLastBidFlash(null);
        break;
      }
      case 'SHOW_ENDED': {
        const payload = message.payload as {
          show_id: string;
          reason: string;
        };
        setShowEnded({ reason: payload.reason });
        setActiveAuction(null);
        setOnDeckItem(null);
        break;
      }
      case 'VIEWER_COUNT': {
        const payload = message.payload as { viewer_count: number };
        setViewerCount(payload.viewer_count);
        break;
      }
      case 'BIN_SALE_STARTED':
      case 'BIN_SALE_STATE': {
        const payload = message.payload as {
          id: string;
          item_id: string;
          base_price: number;
          final_price: number;
          sale_enabled: boolean;
          sale_type?: string;
          sale_amount?: number;
          initial_quantity: number;
          remaining_quantity: number;
          status: string;
          item?: {
            id: string;
            name: string;
            description: string;
            image_url: string;
            starting_price?: number;
            weight_oz?: number;
            item_number?: number;
          };
        };
        // Use functional update to preserve existing item if payload doesn't include it
        setActiveBinSale((prev) => {
          const newItem = payload.item ? {
            id: payload.item.id,
            name: payload.item.name,
            description: payload.item.description || '',
            imageUrl: payload.item.image_url || '',
            startingPrice: payload.item.starting_price || 0,
            weightOz: payload.item.weight_oz,
            itemNumber: payload.item.item_number,
          } : prev?.item; // Preserve existing item if not in payload

          return {
            id: payload.id,
            itemId: payload.item_id,
            basePrice: payload.base_price,
            finalPrice: payload.final_price,
            saleEnabled: payload.sale_enabled,
            saleType: payload.sale_type as 'PERCENT' | 'AMOUNT' | undefined,
            saleAmount: payload.sale_amount,
            initialQuantity: payload.initial_quantity,
            remainingQuantity: payload.remaining_quantity,
            status: payload.status as 'PENDING' | 'LIVE' | 'ENDED' | 'CANCELLED',
            item: newItem,
          };
        });
        setActiveAuction(null);
        setAuctionEnded(null);
        setBinSaleEnded(null);
        break;
      }
      case 'BIN_PURCHASE_SUCCESS': {
        const payload = message.payload as {
          sale_id: string;
          buyer_id: string;
          buyer_username?: string;
          order_id: string;
          price: number;
          remaining_quantity: number;
        };
        // Update remaining quantity
        setActiveBinSale((prev) => {
          if (!prev || prev.id !== payload.sale_id) return prev;
          return {
            ...prev,
            remainingQuantity: payload.remaining_quantity,
          };
        });
        // Update purchase status (only for the buyer who made the purchase)
        setBinPurchaseStatus('success');
        setTimeout(() => setBinPurchaseStatus('idle'), 2000);
        break;
      }
      case 'BIN_PURCHASE_FAILED': {
        const payload = message.payload as {
          sale_id: string;
          buyer_id: string;
          reason: string;
        };
        console.warn('BIN purchase failed:', payload.reason);
        setBinPurchaseStatus('failed');
        setBinPurchaseFailReason(payload.reason);
        setTimeout(() => {
          setBinPurchaseStatus('idle');
          setBinPurchaseFailReason(null);
        }, 3000);
        break;
      }
      case 'BIN_SALE_ENDED': {
        const payload = message.payload as {
          sale_id: string;
          reason: string;
          total_sold: number;
        };
        setBinSaleEnded({
          reason: payload.reason,
          totalSold: payload.total_sold,
        });
        setActiveBinSale(null);
        break;
      }
      case 'GIVEAWAY_STARTED': {
        const payload = message.payload as {
          id: string;
          item_id: string;
          status: string;
          entry_count: number;
          started_at?: string;
          item?: { id: string; name: string; description?: string; image_url?: string; item_number?: number };
        };
        setActiveGiveaway({
          id: payload.id,
          itemId: payload.item_id,
          status: payload.status,
          entryCount: payload.entry_count,
          startedAt: payload.started_at,
          item: payload.item ? {
            id: payload.item.id,
            name: payload.item.name,
            description: payload.item.description || '',
            imageUrl: payload.item.image_url || '',
            startingPrice: 0,
            itemNumber: payload.item.item_number,
          } : undefined,
        });
        setGiveawayEnded(null);
        setGiveawayEntryStatus('idle');
        break;
      }
      case 'GIVEAWAY_ENTRY': {
        const payload = message.payload as {
          giveaway_id: string;
          entry_count: number;
          user_id: string;
          username: string;
        };
        setActiveGiveaway((prev) => {
          if (!prev || prev.id !== payload.giveaway_id) return prev;
          return { ...prev, entryCount: payload.entry_count };
        });
        setGiveawayEntryStatus('entered');
        break;
      }
      case 'GIVEAWAY_ENTRY_FAILED': {
        const payload = message.payload as { giveaway_id: string; reason: string };
        console.warn('Giveaway entry failed:', payload.reason);
        setGiveawayEntryStatus('failed');
        setTimeout(() => setGiveawayEntryStatus('idle'), 3000);
        break;
      }
      case 'GIVEAWAY_ENDED': {
        const payload = message.payload as {
          giveaway_id: string;
          winner_id?: string;
          winner_username?: string;
          entry_count: number;
          cancelled: boolean;
        };
        setGiveawayEnded({
          giveawayId: payload.giveaway_id,
          winnerId: payload.winner_id,
          winnerUsername: payload.winner_username,
          entryCount: payload.entry_count,
          cancelled: payload.cancelled,
        });
        setActiveGiveaway(null);
        break;
      }
      case 'BROADCAST_PAUSED': {
        setBroadcasterPaused(true);
        break;
      }
      case 'BROADCAST_RESUMED': {
        setBroadcasterPaused(false);
        break;
      }
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  // Ping to keep connection alive
  useEffect(() => {
    if (!isConnected) return;

    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'PING', payload: {} }));
      }
    }, 25000); // More frequent pings for buyers

    return () => clearInterval(interval);
  }, [isConnected]);

  // Timer countdown (updates every 100ms for smooth display)
  useEffect(() => {
    if (!activeAuction || activeAuction.millisecondsRemaining <= 0) return;

    const interval = setInterval(() => {
      setActiveAuction((prev) => {
        if (!prev || prev.millisecondsRemaining <= 0) return prev;
        return {
          ...prev,
          millisecondsRemaining: Math.max(0, prev.millisecondsRemaining - 100),
        };
      });
    }, 100);

    return () => clearInterval(interval);
  }, [activeAuction?.id]);

  return {
    isConnected,
    hasReceivedState,
    viewerCount,
    onDeckItem,
    activeAuction,
    activeBinSale,
    recentBids,
    auctionEnded,
    binSaleEnded,
    showEnded,
    bidStatus,
    bidRejectionReason,
    binPurchaseStatus,
    binPurchaseFailReason,
    shippingConfig,
    buyerValidation,
    activeGiveaway,
    giveawayEntryStatus,
    giveawayEnded,
    connect,
    disconnect,
    placeBid,
    userMaxBidAmount,
    lastBidFlash,
    purchaseBin,
    enterGiveaway,
    refreshValidation,
    broadcasterPaused,
  };
}
